import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { engine } from '../core/audio/AudioEngine';
import { sampleBank } from '../core/samples/SampleBank';
import { noteName } from '../core/project/types';
import { useAppStore } from '../state/store';

/**
 * Sample editor strip for sampler channels: sample picker, file import
 * (button or drag & drop), waveform preview, root note, loop toggle.
 */
export function SampleEditor({ channelId }: { channelId: string }) {
  const channel = useAppStore((s) => s.project.channels.find((c) => c.id === channelId));
  const { updateSampler, importSample } = useAppStore.getState();
  useSyncExternalStore(sampleBank.subscribe, sampleBank.getVersion);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const sampler = channel?.kind === 'sampler' ? channel.sampler : null;
  const sample = sampler ? sampleBank.get(sampler.sampleId) : undefined;

  // Waveform (min/max per pixel column).
  useEffect(() => {
    const canvas = canvasRef.current;
    const c2d = canvas?.getContext('2d');
    if (!canvas || !c2d) return;
    const w = canvas.width;
    const h = canvas.height;
    c2d.clearRect(0, 0, w, h);
    c2d.fillStyle = '#0d1017';
    c2d.fillRect(0, 0, w, h);
    if (!sample || !channel) return;
    const data = sample.buffer.getChannelData(0);
    const step = Math.max(1, Math.floor(data.length / w));
    c2d.fillStyle = channel.color;
    for (let x = 0; x < w; x++) {
      let min = 1;
      let max = -1;
      const off = x * step;
      for (let i = 0; i < step && off + i < data.length; i++) {
        const v = data[off + i];
        if (v < min) min = v;
        if (v > max) max = v;
      }
      const y1 = ((1 - max) / 2) * h;
      const y2 = ((1 - min) / 2) * h;
      c2d.fillRect(x, y1, 1, Math.max(1, y2 - y1));
    }
  }, [sample, channel]);

  if (!channel || !sampler) return null;

  const onFiles = (files: FileList | null) => {
    const f = files?.[0];
    if (f) void importSample(f);
  };

  return (
    <div
      className={`sample-editor ${dragOver ? 'dragover' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onFiles(e.dataTransfer.files);
      }}
    >
      <div className="se-row">
        <label className="se-label">Sample</label>
        <select
          className="se-select"
          value={sampler.sampleId ?? ''}
          onChange={(e) => updateSampler(channelId, { sampleId: e.target.value || null })}
        >
          {sampleBank.list().map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <button className="pill-btn" onClick={() => fileRef.current?.click()} title="Import an audio file">
          Load…
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="audio/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            onFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <button
          className="pill-btn"
          title="Preview at root note"
          onClick={() => {
            if (sample) {
              void engine.ensureContext().then(() => engine.noteOn(channelId, sample.rootNote));
            }
          }}
        >
          ▶
        </button>
        <label className="se-label">Root</label>
        <select
          className="se-select se-root"
          value={sampler.rootNote}
          onChange={(e) => updateSampler(channelId, { rootNote: Number(e.target.value) })}
        >
          {Array.from({ length: 49 }, (_, i) => 36 + i).map((n) => (
            <option key={n} value={n}>
              {noteName(n)}
            </option>
          ))}
        </select>
        <button
          className={`pill-btn ${sampler.loop ? 'active' : ''}`}
          onClick={() => updateSampler(channelId, { loop: !sampler.loop })}
          title="Loop the sample while a note is held"
        >
          Loop
        </button>
        <span className="se-info">
          {sample
            ? `${sample.buffer.duration.toFixed(2)} s · ${sample.buffer.sampleRate} Hz`
            : 'no sample — pick one or drag a file here'}
        </span>
      </div>
      <canvas ref={canvasRef} className="se-wave" width={900} height={64} />
    </div>
  );
}